import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'

import useFetch from 'src/hooks/useFetch'
import useProductPage from 'src/hooks/useProductPage'
import useTracking from 'src/hooks/useTracking'
import { useAppSelector } from 'src/redux/hooks'
import { getApiUrl } from 'src/utils'
import { ExportConfig } from 'src/utils/export'
import { ComponentCode } from 'src/utils/settings'
import { trackSchedulingFormValues } from 'src/utils/tracking'
import ExportSchedulingForm from '../../../ui/organisms/ExportScheduling/ExportSchedulingForm'

interface FormValues {
  format: string
  emails: ReadonlyArray<string>
  title: string
  message: string
  frequency: { label: string; value: string }
  sub_frequency: { label: string; value: string | number }
  delivery_duration: { label: string; value: number }
}

interface DefaultValue {
  frequency: { label: string; value: string | number }
  subFrequency?: any
}

interface ExportSchedulingContainerProps {
  selectedFrequency?: string
  exportDashboardConfig: ExportConfig
  isOpen?: boolean
  onClose?: () => void
  className?: string
}

export const ExportSchedulingContainer = ({
  selectedFrequency,
  exportDashboardConfig,
  isOpen,
  onClose,
}: ExportSchedulingContainerProps) => {
  const [defaultFrequency, setDefaultFrequency] = useState<DefaultValue>()
  const page = useProductPage()
  const settings = useAppSelector(s => s.componentsSettings.settings[page.code] || {})
  const filterBarSettings = settings[ComponentCode.FilterBar] as any
  const { trackEvent } = useTracking()

  const [, executeExportSchedulingRequest] = useFetch(
    { url: `${getApiUrl()}/api/admin/export_configs`, method: 'POST' },
    { manual: true },
  )

  const exportSchedulingRequest = async (data: any) => {
    try {
      await executeExportSchedulingRequest({
        data: {
          export_config: {
            format: data.format,
            email_informations: {
              recipients: data.emails,
              title: data.title,
              message: data.message,
            },
            scheduling_informations: {
              frequency: data.frequency.value,
              sub_frequency: data.sub_frequency?.value,
              delivery_duration: data.delivery_duration?.value,
            },
            target_period_informations: {
              compare_to: filterBarSettings?.timePeriod?.selectedRangeCompare,
              period: filterBarSettings?.timePeriod?.selectedRange,
              frequency:
                filterBarSettings?.timePeriod.frequency || filterBarSettings?.timePeriod.view,
              custom_period: {
                start_date: filterBarSettings?.timePeriod.startDate,
                end_date: filterBarSettings?.timePeriod.endDate,
                previous_start_date: filterBarSettings?.timePeriod.startDateCompare,
                previous_end_date: filterBarSettings?.timePeriod.endDateCompare,
              },
            },
            sub_product: page.code,
            scopes: filterBarSettings.scopes,
            breakdown: filterBarSettings?.breakdown,
            components_settings: {
              [page.code]: settings,
            },
          },
        },
      })
      toast.success('Scheduled dashboard successfully created')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error({ error })
      toast.error('An error occurred. Please retry.')
    } finally {
      onClose?.()
    }
  }

  const onSubmitExportSchedulingForm = ({
    format,
    emails,
    title,
    message,
    frequency,
    sub_frequency,
    delivery_duration,
  }: FormValues) => {
    const formValues = {
      format,
      emails,
      title,
      message,
      frequency,
      sub_frequency,
      delivery_duration,
    }
    const trackedValues = (({ frequency, sub_frequency, delivery_duration }) => ({
      emails,
      frequency,
      sub_frequency,
      delivery_duration,
    }))(formValues)

    trackEvent({ action: 'Click: Share validate' })
    trackSchedulingFormValues(trackedValues, trackEvent)
    return exportSchedulingRequest(formValues)
  }

  useEffect(() => {
    isOpen && trackEvent({ action: 'Click: Share icon' })
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <ExportSchedulingForm
      onSubmit={onSubmitExportSchedulingForm}
      initialValues={{
        emails: [],
        frequency: defaultFrequency?.frequency,
        format: 'pdf',
        sub_frequency: defaultFrequency?.subFrequency,
      }}
      filterBarSettings={filterBarSettings}
      onClose={onClose}
      setDefaultFrequency={setDefaultFrequency}
      selectedFrequency={selectedFrequency}
      exportDashboardConfig={exportDashboardConfig}
      page={page}
    />
  )
}

const mapStateToProps = (state: any) => ({
  selectedFrequency: formValueSelector('ExportSchedulingForm')(state, 'frequency'),
})
const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(ExportSchedulingContainer)
